/**
 * Order history for one late-to-raise row (CR-8623): every PO line the site
 * has placed for the part, newest first, with a tab per supplier — the
 * POLineDrilldownModal shell (tabs with counts, Export to Excel, pager).
 *
 * The price change compares a line with the same supplier's previous order
 * that was not cancelled, in the same currency and unit (worked out on the
 * server). Clicking a line opens it on PO Lines in a new browser tab.
 */
import { useEffect, useState } from 'react';
import {
  lateToRaiseAPI,
  type LateToRaiseRow,
  type OrderHistoryLine,
  type OrderHistorySupplier,
} from '../../../../lib/api';
import { useFormatters } from '../../../../contexts/PreferencesContext';
import { useView } from '../../../../contexts/ViewContext';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../../ui/dialog';
import { Button } from '../../../ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../ui/table';
import { DrilldownPager } from '../../../dashboard/po-lines/DrilldownPager';
import { Download, ExternalLink, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { LateBadge } from './LateBadge';

const PAGE_SIZE = 50;

interface OrderHistoryModalProps {
  /** The row whose history to show; null = closed. */
  row: LateToRaiseRow | null;
  onClose: () => void;
}

const STATUS_TONE: Record<string, string> = {
  received: 'bg-green-100 text-green-800',
  partially_received: 'bg-blue-100 text-blue-800',
  pending: 'bg-blue-100 text-blue-800',
  cancelled: 'bg-slate-100 text-slate-600',
};

/** Where a PO line opens: the PO Lines tab's existing deep link. */
export const poLineHref = (lineId: number) => `/purchasing?tab=po_lines&line=${lineId}`;

export default function OrderHistoryModal({ row, onClose }: OrderHistoryModalProps) {
  const { formatDate, formatCurrency, formatNumber } = useFormatters();
  // The row was listed under the current view, so its history is asked for under it too.
  const { getViewQueryParams } = useView();
  const [supplier, setSupplier] = useState<number | null>(null);
  const [page, setPage] = useState(1);
  const [lines, setLines] = useState<OrderHistoryLine[]>([]);
  const [suppliers, setSuppliers] = useState<OrderHistorySupplier[]>([]);
  const [total, setTotal] = useState(0);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  // A new row never shows the last row's lines, prices or suppliers, not even while it loads.
  const clearHistory = () => { setLines([]); setSuppliers([]); setTotal(0); setCount(0); };
  useEffect(() => { setSupplier(null); setPage(1); clearHistory(); }, [row?.id]);

  useEffect(() => {
    if (!row) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    lateToRaiseAPI.history(row.id, { ...getViewQueryParams(), supplier: supplier ?? undefined, page, page_size: PAGE_SIZE })
      .then((res) => {
        if (cancelled) return;
        setLines(res.data.results);
        setSuppliers(res.data.suppliers);
        setTotal(res.data.total);
        setCount(res.data.count);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        console.error('Order history load failed:', err);
        clearHistory();
        setError(err instanceof Error ? err.message : 'Failed to load the order history');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row, supplier, page]);

  const handleExport = async () => {
    if (!row) return;
    setExporting(true);
    try {
      const resp = await lateToRaiseAPI.historyExport(row.id, { ...getViewQueryParams(), supplier: supplier ?? undefined });
      const blob = new Blob([resp.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `order-history-${row.part_number}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Excel export ready');
    } catch (err: unknown) {
      console.error('Order history export failed:', err);
      toast.error('Failed to export Excel');
    } finally {
      setExporting(false);
    }
  };

  const selectTab = (id: number | null) => { setSupplier(id); setPage(1); };
  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));

  const priceChange = (line: OrderHistoryLine) => {
    if (line.price_change === null) return null;
    const delta = Number(line.price_change);
    if (Math.abs(delta) < 0.005) return null;
    const up = delta > 0;
    return (
      <span className={`ml-1 text-xs ${up ? 'text-red-700' : 'text-emerald-700'}`}>
        {up ? '▲' : '▼'} {formatCurrency(Math.abs(delta), line.currency || undefined, 2)}
      </span>
    );
  };

  return (
    <Dialog open={row !== null} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="!max-w-[min(1100px,95vw)] w-[min(1100px,95vw)]">
        <DialogHeader>
          {/* pr-8 keeps Export clear of the dialog's own close button. */}
          <div className="flex items-start justify-between gap-3 pr-8">
            <div>
              <DialogTitle>
                Order history: {row?.part_number}{row?.description ? `, ${row.description}` : ''}
              </DialogTitle>
              {row && (
                <DialogDescription className="flex flex-wrap items-center gap-x-4 gap-y-1">
                  <span>To order <b className="text-foreground">{formatNumber(Number(row.qty_to_order))} {row.unit_of_measure}</b></span>
                  <span>Order by <b className="text-foreground">{formatDate(row.order_by)}</b></span>
                  <span><LateBadge days={row.days_late} /> late</span>
                </DialogDescription>
              )}
            </div>
            <Button variant="outline" size="sm" onClick={handleExport} disabled={exporting || count === 0}>
              {exporting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
              Export to Excel
            </Button>
          </div>
        </DialogHeader>

        {total > 0 && (
          <div className="flex flex-wrap gap-1 border-b pb-2" role="tablist" aria-label="Suppliers">
            {[{ id: null as number | null, name: 'All suppliers', count: total }, ...suppliers].map((s) => {
              const active = s.id === supplier;
              return (
                <button
                  key={s.id ?? 'all'}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => selectTab(s.id)}
                  className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                    active ? 'bg-primary text-primary-foreground font-medium' : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  {s.name || 'No supplier'}<span className="ml-1 tabular-nums opacity-80">({s.count})</span>
                </button>
              );
            })}
          </div>
        )}

        {error && <div className="py-3 text-sm text-red-600">{error}</div>}

        {loading && lines.length === 0 ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        ) : total === 0 && !loading ? (
          <div className="py-10 text-center text-sm text-muted-foreground">
            This part has never been ordered at this site, so there is no supplier or price to go on.
          </div>
        ) : (
          <div className="max-h-[60vh] overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>PO Number</TableHead>
                  <TableHead className="text-right">Line</TableHead>
                  <TableHead>Supplier</TableHead>
                  <TableHead>Ordered</TableHead>
                  <TableHead className="text-right">Qty</TableHead>
                  <TableHead className="text-right">Unit Price</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Received</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {lines.map((line) => (
                  <TableRow
                    key={line.id}
                    className="cursor-pointer"
                    title="Open this PO line in a new tab"
                    onClick={() => window.open(poLineHref(line.id), '_blank', 'noopener')}
                  >
                    <TableCell className="whitespace-nowrap font-mono text-sm">
                      <a
                        href={poLineHref(line.id)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-primary hover:underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {line.po_number}<ExternalLink className="h-3 w-3" />
                      </a>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{line.line_number}</TableCell>
                    <TableCell>{line.supplier_name}</TableCell>
                    <TableCell className="whitespace-nowrap">{line.issue_date ? formatDate(line.issue_date) : '-'}</TableCell>
                    <TableCell className="text-right tabular-nums">{formatNumber(Number(line.quantity))} {line.unit_of_measure}</TableCell>
                    <TableCell className="whitespace-nowrap text-right tabular-nums">
                      {formatCurrency(Number(line.unit_price), line.currency || undefined, 2)}{priceChange(line)}
                    </TableCell>
                    <TableCell>
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_TONE[line.status] ?? 'bg-slate-100 text-slate-600'}`}>
                        {line.status_label}
                      </span>
                    </TableCell>
                    <TableCell className="whitespace-nowrap">{line.received_date ? formatDate(line.received_date) : <span className="text-muted-foreground">-</span>}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>{count.toLocaleString()} PO line{count === 1 ? '' : 's'}, newest first</span>
          {count > PAGE_SIZE && (
            <DrilldownPager page={page} totalPages={totalPages} loading={loading} onPageChange={setPage} idPrefix="ohm" />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
